/* import __COLOCATED_TEMPLATE__ from './ticket-assignment-limit.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { post } from '../../../lib/ajax';
import { action } from '@ember/object';
import { ref } from 'ember-ref-bucket';
import type IntlService from 'embercom/services/intl';
import type AppConversationCapacityLimit from 'embercom/models/app-conversation-capacity-limit';

interface Args {
  admin?: any;
  workspaceSettings?: AppConversationCapacityLimit;
}

export default class TicketAssignmentLimit extends Component<Args> {
  @service declare permissionsService: any;
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare intercomConfirmService: any;
  @service declare intercomEventService: any;
  @service declare modelDataCacheService: any;

  @tracked ticketAssignmentLimit =
    this.args.admin?.workload_management_settings?.ticket_assignment_limit ?? '';
  @tracked isEditingTicketAssignmentLimit = false;
  @tracked valueIsModified = false;

  @ref('ticket-assignment-limit-input') inputElement: any;

  get canChangeTicketAssignmentLimit() {
    return this.permissionsService.currentAdminCan('can_manage_workload_management');
  }

  get ticketAssignmentLimitToInt() {
    return parseInt(this.ticketAssignmentLimit, 10);
  }

  get adminTicketAssignmentLimit() {
    return this.args.admin?.workload_management_settings?.ticket_assignment_limit;
  }

  get ticketAssignmentLimitHasChanged() {
    return this.valueIsModified;
  }

  get ticketAssignmentLimitInvalid() {
    return (
      !this.ticketAssignmentLimit ||
      this.ticketAssignmentLimitToInt > 1_000_000 ||
      this.ticketAssignmentLimitToInt < 0
    );
  }

  get disableSubmitButton() {
    return this.ticketAssignmentLimitInvalid || !this.ticketAssignmentLimitHasChanged;
  }

  get currentWorkspaceLimit() {
    if (this.args.workspaceSettings?.usesCustomTicketAssignmentLimit) {
      return this.args.workspaceSettings?.ticketAssignmentLimit;
    }

    return this.args.workspaceSettings?.capacityLimit;
  }

  get shouldShowEmptyCell(): boolean {
    return (
      this.args.admin?.workload_management_settings?.ticket_assignment_limit_enabled &&
      !this.args.admin?.workload_management_settings?.ticket_assignment_disabled
    );
  }

  @action
  async changeTicketAssignmentLimit() {
    let revertToWorkspace = this.ticketAssignmentLimit === this.currentWorkspaceLimit;

    // prevent submitting invalid ticket assignment limit
    if (
      (this.ticketAssignmentLimitInvalid || !this.ticketAssignmentLimitHasChanged) &&
      !revertToWorkspace
    ) {
      return;
    }

    // null reverts to workspace default
    let ticket_assignment_limit = !revertToWorkspace ? this.ticketAssignmentLimitToInt : null;

    try {
      await post(`/ember/admins/change_ticket_assignment_limit`, {
        ticket_assignment_limit,
        admin_ids: [this.args.admin.id],
        id: this.appService.app.currentAdmin.id,
        app_id: this.appService.app.id,
      });

      this.args.admin.workload_management_settings = {
        ...this.args.admin.workload_management_settings,
        ticket_assignment_limit,
      };

      if (!revertToWorkspace) {
        this.notificationsService.notifyConfirmation(
          this.intl.t('settings.teammate-workload.ticket-assignment-limit.success', {
            teammate: this.args.admin?.name,
          }),
        );
      } else {
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'settings.teammate-workload.ticket-assignment-limit.revert-to-workspace-success',
            {
              teammate: this.args.admin?.name,
              workspaceLimit: this.currentWorkspaceLimit,
            },
          ),
        );
      }
      this.resetState();
    } catch (error: any) {
      if (error.jqXHR) {
        this.notificationsService.notifyError(error.jqXHR.responseJSON.errors);
      }
    } finally {
      this.appService.app.updateLocalCache(this.modelDataCacheService);
      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'ticket_assignment_limit',
        place: 'teammate_workload',
        teammate_id: this.args.admin.id,
        ticket_assignment_limit,
      });
    }
  }

  @action unfocusInput(focusEvent: any) {
    if (focusEvent.relatedTarget?.type === 'submit') {
      return;
    }

    if (!this.ticketAssignmentLimitHasChanged) {
      return;
    }

    this.triggerConfirmModal();
  }

  @action async triggerConfirmModal() {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('settings.teammate-workload.ticket-assignment-confirm-modal.title'),
      body: this.intl.t('settings.teammate-workload.ticket-assignment-confirm-modal.body', {
        name: this.args.admin.name,
      }),
      cancelButtonText: this.intl.t(
        'settings.teammate-workload.ticket-assignment-confirm-modal.cancel-button',
      ),
      confirmButtonText: this.intl.t(
        'settings.teammate-workload.ticket-assignment-confirm-modal.confirm-button',
      ),
    });

    if (confirmed) {
      this.resetState();
    } else {
      this.inputElement.focus();
    }
  }

  @action
  async revertToWorkspaceLimit() {
    this.ticketAssignmentLimit = this.currentWorkspaceLimit;
    await this.changeTicketAssignmentLimit();
  }

  resetState() {
    this.isEditingTicketAssignmentLimit = false;
    this.ticketAssignmentLimit =
      this.args.admin?.workload_management_settings?.ticket_assignment_limit || '';
    this.valueIsModified = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::TeammateWorkload::TicketAssignmentLimit': typeof TicketAssignmentLimit;
    'inbox/teammate-workload/ticket-assignment-limit': typeof TicketAssignmentLimit;
  }
}
